// Runtime native-bridge detection. The same React/TS code runs in
// three places:
//   1. Browser (desktop / mobile Safari / Android Chrome)
//   2. iOS Capacitor WKWebView shell
//   3. Android Capacitor WebView shell (future)
//
// Capacitor injects a global `Capacitor` object into the WebView the
// instant the bridge boots. We detect via that — no build-time flags,
// no two-bundle complexity. Code calling these helpers just gets
// native behavior for free when the user installs the App Store
// version, and falls back to web APIs on the browser.

import { api } from "./api";

// We type the Capacitor surface area very narrowly — only the bits we
// touch. The real types come from @capacitor/* if you ever want them.
interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
}

declare global {
  interface Window {
    Capacitor?: CapacitorGlobal;
  }
}

export function isNative(): boolean {
  return Boolean(window.Capacitor?.isNativePlatform?.());
}

// ─── Keyboard ──────────────────────────────────────────────────────────
// Hides the iOS WKWebView's "Previous / Next / Done" form-nav accessory
// bar that floats above the on-screen keyboard whenever an input is
// focused. Apple adds it automatically; for a focused, app-like UI it
// reads as platform clutter, especially on forms with their own
// keyboard-dismiss controls. resize: "native" tells Capacitor to
// shrink the WebView when the keyboard appears (instead of the page
// scrolling under a fixed-height WebView), so sticky bottom bars stay
// above the keyboard. No-op on web.
export async function initNativeKeyboard(): Promise<void> {
  if (!isNative()) return;
  try {
    const mod = await import("@capacitor/keyboard");
    const { Keyboard, KeyboardResize } = mod;
    // iOS only — Android's IME doesn't have an accessory bar concept.
    if (nativePlatform() === "ios") {
      await Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(
        () => {
          /* older iOS versions can throw; harmless */
        },
      );
    }
    await Keyboard.setResizeMode({ mode: KeyboardResize.Native }).catch(
      () => {
        /* falls back to default */
      },
    );
  } catch {
    /* Plugin not installed (web bundle) or runtime error — keyboard
       behavior just stays at OS defaults. */
  }
}

export function nativePlatform(): "ios" | "android" | "web" {
  const p = window.Capacitor?.getPlatform?.() ?? "web";
  return p === "ios" || p === "android" ? p : "web";
}

// ─── Geolocation ────────────────────────────────────────────────────────

export interface NativePosition {
  lat: number;
  lng: number;
  accuracy_m: number | null;
  speed_mps: number | null;
  heading_deg: number | null;
  at: number; // epoch ms
}

type PositionCallback = (pos: NativePosition) => void;
type ErrorCallback = (err: { code: string; message: string }) => void;

interface WatchHandle {
  stop: () => Promise<void>;
}

/**
 * Subscribe to position updates. On iOS this hits CoreLocation through
 * Capacitor's Geolocation plugin (much more accurate, better battery,
 * works while backgrounded if the user grants Always permission). On
 * the web it falls back to navigator.geolocation.watchPosition().
 *
 * Returns a handle whose .stop() unsubscribes. Always call .stop() on
 * unmount or you'll leak a CoreLocation watcher.
 */
export async function watchPosition(
  onUpdate: PositionCallback,
  onError?: ErrorCallback,
): Promise<WatchHandle | null> {
  if (isNative()) {
    try {
      const mod = await import("@capacitor/geolocation");
      const Geolocation = mod.Geolocation;

      // Ask for permission once. On iOS this triggers the system
      // dialog with NSLocationWhenInUseUsageDescription as the body.
      const perm = await Geolocation.requestPermissions({
        permissions: ["location"],
      });
      if (perm.location !== "granted") {
        onError?.({ code: "PERMISSION_DENIED", message: perm.location });
        return null;
      }

      const watchId = await Geolocation.watchPosition(
        { enableHighAccuracy: true, timeout: 15_000 },
        (pos, err) => {
          if (err) {
            onError?.({ code: "WATCH_ERROR", message: err.message ?? "unknown" });
            return;
          }
          if (!pos) return;
          onUpdate({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy_m: pos.coords.accuracy ?? null,
            speed_mps: pos.coords.speed ?? null,
            heading_deg: pos.coords.heading ?? null,
            at: pos.timestamp ?? Date.now(),
          });
        },
      );
      return {
        stop: async () => {
          try {
            await Geolocation.clearWatch({ id: watchId });
          } catch {
            // best-effort
          }
        },
      };
    } catch (err) {
      onError?.({
        code: "PLUGIN_LOAD_FAIL",
        message: err instanceof Error ? err.message : String(err),
      });
      return null;
    }
  }

  // Web fallback — same shape as the native handle so callers don't
  // branch.
  if (!navigator.geolocation) {
    onError?.({ code: "UNSUPPORTED", message: "Geolocation not available" });
    return null;
  }
  const id = navigator.geolocation.watchPosition(
    (pos) =>
      onUpdate({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy_m: pos.coords.accuracy ?? null,
        speed_mps: pos.coords.speed ?? null,
        heading_deg: pos.coords.heading ?? null,
        at: pos.timestamp ?? Date.now(),
      }),
    (err) =>
      onError?.({
        code: err.code === 1 ? "PERMISSION_DENIED" : "WATCH_ERROR",
        message: err.message,
      }),
    { enableHighAccuracy: true, timeout: 15_000, maximumAge: 5_000 },
  );
  return {
    stop: async () => navigator.geolocation.clearWatch(id),
  };
}

// ─── Push notifications ─────────────────────────────────────────────────

let pushInitialized = false;

/**
 * Call once after sign-in. Asks the user for permission (native dialog
 * on iOS), registers with APNs, and posts the device token to our
 * /api/push/register endpoint so the backend can later fire pushes.
 * Safe to call repeatedly — it no-ops on re-entry.
 */
export async function initPushNotifications(): Promise<void> {
  if (!isNative() || pushInitialized) return;
  pushInitialized = true;

  try {
    const mod = await import("@capacitor/push-notifications");
    const PushNotifications = mod.PushNotifications;

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== "granted") return;

    // Apple hands us the device token via the 'registration' listener.
    // Persist it on the backend so future notification creates can
    // send a push to this device.
    PushNotifications.addListener("registration", async (token) => {
      try {
        const appMod = await import("@capacitor/app");
        const info = await appMod.App.getInfo().catch(() => null);
        await api("/api/push/register", {
          method: "POST",
          body: {
            token: token.value,
            platform: nativePlatform(),
            app_version: info?.version ?? null,
            os_version: null,
            device_name: null,
          },
        });
      } catch {
        // Don't crash the app over a failed registration — the bell still
        // works without push.
      }
    });

    PushNotifications.addListener("registrationError", (err) => {
      // eslint-disable-next-line no-console
      console.warn("[push] registration error:", err);
    });

    // Tapping a notification while the app is closed routes the user to
    // the deep-link in the payload's `link` field.
    PushNotifications.addListener("pushNotificationActionPerformed", (event) => {
      const data = event.notification?.data as { link?: string } | undefined;
      const link = data?.link;
      if (link && typeof link === "string") {
        // History API so React Router picks it up without a full reload.
        window.history.pushState({}, "", link);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
    });

    await PushNotifications.register();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[push] init failed:", err);
  }
}

/** Unregister on sign-out so the next person on this device doesn't
 *  receive notifications meant for the previous account. */
export async function unregisterPushNotifications(): Promise<void> {
  if (!isNative()) return;
  try {
    const mod = await import("@capacitor/push-notifications");
    // Capacitor doesn't expose a single "give me the token" call after
    // init, so we just clear our local flag and let the backend's
    // soft-delete-on-signout path handle the row. (On sign-back-in the
    // next register() call upserts a fresh row.)
    await mod.PushNotifications.removeAllListeners();
    pushInitialized = false;
  } catch {
    // best-effort
  }
}

// ─── Splash screen ──────────────────────────────────────────────────────

/** Hide the iOS launch splash once React has painted the first screen. */
export async function hideNativeSplash(): Promise<void> {
  if (!isNative()) return;
  try {
    const mod = await import("@capacitor/splash-screen");
    await mod.SplashScreen.hide();
  } catch {
    // best-effort — Capacitor auto-hides after a few seconds if we miss.
  }
}
